'use client';

import { Play, Trash } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Card } from '@kit/ui/card';
import { Trans } from '@kit/ui/trans';

import { UploadFileResult, useFileUploader } from '../hooks/use-file-uploader';

/**
 * Props for the UploadFileResumeWrapper component
 */
type UploadFileResumeWrapperProps = {
  /**
   * Callback when a resumed upload completes
   */
  onComplete?: (results: UploadFileResult) => void;

  /**
   * The key of the upload session
   */
  uploadKey: string;
};

/**
 * Component that displays a list of paused or failed uploads that can be resumed.
 * It will only render if there are files that can be resumed for the given uploadKey.
 *
 * @example
 * ```tsx
 * <UploadFileResumeWrapper
 *   uploadKey="project-123"
 *   onComplete={(result) => {
 *     console.log('Resume complete:', result);
 *   }}
 * />
 * ```
 */
export function UploadFileResumeWrapper({
  onComplete,
  uploadKey,
}: UploadFileResumeWrapperProps) {
  const { files, resumeUpload, removeFile } = useFileUploader();

  // Filter files that can be resumed for this upload key
  const pausedFiles = files.filter(
    (file) =>
      file.uploadKey === uploadKey &&
      (file.status === 'paused' || file.status === 'error'),
  );

  // Don't render anything if there are no paused files
  if (pausedFiles.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-2">
      <p className="text-muted-foreground text-sm">
        <Trans i18nKey="views:uploadManager.description" />
      </p>
      {pausedFiles.map((file) => (
        <Card key={file.id} className="flex items-center justify-between p-3">
          <div className="flex-1 truncate">
            <p className="truncate text-sm font-medium">{file.fileName}</p>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span>{(file.fileSize / (1024 * 1024)).toFixed(1)} MB</span>
              <span>•</span>
              <span>
                <Trans i18nKey="views:uploadManager.progress" />{' '}
                {Math.round(file.progress)}%
              </span>
            </div>
          </div>

          <Button
            size="sm"
            variant="ghost"
            className="ml-2"
            onClick={() => resumeUpload(file, onComplete)}
          >
            <Play className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="ml-2"
            onClick={() => removeFile(file.id)}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </Card>
      ))}
    </div>
  );
}
